// ──────────────────────────────────────────────────────────────────────────
// receiptCopy.js — single source of truth for every user-facing string in
// the Receipts tab (Phase 1 of the receipt UI overhaul, 2026-06-27).
//
// Cimon and Claude co-authored these rewrites via a two-pass copy audit
// (receipts_tab_copy_audit_v2.xlsx). The goal: replace inline developer-
// speak with bookkeeper-friendly language. Forbidden words inside this
// module: principal, cascade, PO subtotal, tag, downstream, vendor_payments,
// payment_type, override, idempotent, replay (renamed → refresh),
// "bill as fee" / "bill as adj" / "don't bill" (use plain English).
//
// All strings are pure data. Helpers that compose strings live as small
// functions (no React imports). Components consume by key, never by
// inline string literal.
// ──────────────────────────────────────────────────────────────────────────

// ── Approve-button gate tooltips (rendered on disabled Approve) ─────────
export var approveGate = {
  noKind: 'What type of receipt is this — vendor, client reimbursable, or office receipt?',
  noPO: 'Link this receipt to a PO before approving. Use the Linked PO picker below.',
  noCategory: 'What type of receipt is this — vendor, client reimbursable, or office receipt?',
  noItem: 'Link this receipt to the item it paid for before approving — pick it on the Item & details step.',
  // Fee-confirmation gate (2026-07-02). labels is the list of
  // human-readable fee categories still awaiting confirmation
  // (e.g. ['Freight', 'Tariff']).
  feesUnconfirmed: function(labels) {
    var list = (labels || []).join(', ');
    return 'Confirm the fees before approving — go to the Fee details ' +
      'step and click Confirm fees. Still waiting on: ' +
      (list || 'the estimated fees') + '.';
  },
};

// ── Fee confirmation (Fee details step, 2026-07-02) ─────────────────────
// The fee inputs open pre-filled with the fees estimated on the PO. The
// bookkeeper either confirms them as-is (one click) or corrects any
// amount to match the vendor's bill — then confirms.
// Reworded 2026-08-23 (Cimon: the user must be told exactly what's
// going on — is a fee inside the total or on top? is the system
// applying something from the PO?). The panel now asks "is this fee on
// this receipt?" per row; this block closes the step.
export var feeConfirm = {
  prompt: 'These answers match what’s printed on the receipt.',
  button: 'Confirm — matches the receipt',
  confirmedNote: 'Fees confirmed — saved with this receipt.',
};

// ── Wizard completion — role-aware (2026-07-02, Cimon's directive) ──────
// Staff enter receipts but cannot approve them (approval is owner/
// bookkeeper only). Pre-fix, the final wizard step told everyone to
// "Click Approve in the toolbar above" — a dead end for staff, who have
// no such button. Non-approvers get a "Save & close" completion instead:
// every field already autosaves via patch() as they type/pick, so
// closing the wizard IS the "I'm done" signal. No status change — the
// receipt stays pending/needs_link for an approver to review.
export var wizardComplete = {
  saveAndCloseButton: 'Save & close',
  // Heading + note shown on the final step's summary panel for
  // non-approvers, replacing the approver's "Confirm + approve" /
  // "Click Approve..." text (which staff can't act on).
  nonApproverHeading: 'All set',
  nonApproverNote: 'All saved. This receipt is ready for review — Cimon ' +
    'or the bookkeeper will confirm the details and approve it.',
  // Footer hint (bottom of the wizard) pointing at the toolbar button —
  // mirrors the approver's "Use the Approve button in the toolbar above."
  nonApproverFooterHint: 'Click Save & close above once everything looks right.',
};

// ── Banners (rendered above the Approve button) ─────────────────────────
// principalMisclass — fires when a vendor_fee receipt looks like a
// principal payment (≥ 30% of PO subtotal, no prior payments). Picks
// up the suggested kind from detectKindFromContext.
export function principalMisclassBanner(opts) {
  var amount = (opts && opts.amount) || 0;
  var ratio = (opts && opts.ratioPct) || 0;
  return [
    "Heads up — this receipt is for roughly the full PO amount " +
    "(" + ratio + "% of the PO total), but you've marked it as a fee " +
    "(shipping, freight, tariff, customs). That doesn't add up — fees " +
    "are usually small relative to the PO.",
    "",
    "Is this receipt for:",
    "  • A FULL 100% payment to the vendor (for the goods themselves)?",
    "  • A deposit or final payment to the vendor?",
    "  • A shipping, freight, or other fee?",
    "",
    "It looks like a FULL payment to the vendor. Click to switch to that, " +
    "or type a reason below if you really do mean to mark it as a fee.",
  ].join('\n');
}

// destructiveKind — fires when the picked kind would double-count
// (e.g., Full Payment on a PO that already has prior payments — Hewn case).

// kindLocked — fires when receipt is already approved + cascade has run.
// Staff copy points at Cimon; owners/admins get the variant that points
// at the in-place correction panel (2026-07-13 — the previous copy told
// Cimon to contact Cimon).
export var kindLocked = "This receipt has already been approved and the " +
  "payment is recorded. You can't change the payment type here " +
  "(deposit / final / full / fee). If you marked it wrong, contact Cimon — " +
  "she'll fix it directly in the system.";

export var kindLockedOwner = "This receipt is approved and its payment is " +
  "recorded, so the type is locked here — changing it would desync the " +
  "ledger. Use \u201cCorrect recorded payment\u201d below: it updates the recorded " +
  "payment in place (amount and type together) and can never record a " +
  "second payment.";

// kindHint — fires when running-tab math suggests a different kind.
// label is the human-readable kind name (e.g. "Full payment").
export function kindHint(label) {
  return "Looks like a " + (label || '___') + " based on the receipt amount " +
    "and the running tab. Click to use that instead.";
}

// ── Field labels ─────────────────────────────────────────────────────────
export var label = {
  overrideReasonForKind: function(kindLabelText) {
    return "Why are you marking this as a " + (kindLabelText || 'fee') + "?";
  },
  overrideReasonHelp: function(amount, ratioPct) {
    return "The amount on this receipt looks like the main payment for " +
      "the goods (~$" + amount + " — about " + ratioPct + "% of the PO total). " +
      "If this is really a fee bill, type a short reason so we know it's intentional.";
  },
  refreshCascade: 'Refresh',
  // "Vendor — Final payment" Kind card description (the multi-final
  // clarification — Cimon's late ask, 2026-06-27).
  vendorFinalCardDescription:
    'Payment for items that are ready to ship. A single PO can have ' +
    'multiple final payments if items ship in batches.',
};

// ── Tooltips on data fields ──────────────────────────────────────────────
export var tooltip = {
  amountActual: 'Receipt total (includes all vendor charges + CC fee). Before client markup.',
  refreshCascade:
    'Refresh the vendor payment for this receipt. Use this if you edited ' +
    'the CC fee or amount after approval. Safe — won\'t double-charge.',
  ccFee: "The bank's CC processing fee on this payment. Will pass through to " +
    "the client's final invoice.",
  // "Bank total" was in-house shorthand — not a term everyone knows
  // (Cimon, 2026-09-07). Now "Receipt total", matching how
  // tooltip.amountActual above already uses the phrase, with the
  // definition sitting in the label itself.
  bankTotal:
    'Receipt total = vendor amount + CC fee. The all-in figure, which is ' +
    'what appears on the card statement. Type it here and the vendor ' +
    'amount fills in, or type the vendor amount above and this fills in.',
};

// ── Alerts (window.alert text) ───────────────────────────────────────────
// All alerts are prefixed by the greeting helper in ReceiptsTab.jsx; the
// strings here are the BODY only. The greeting is concatenated by the
// caller.
export var alert = {
  datePaidMissing: function() {
    return ' \u2014 fill in the Date paid before approving. That\'s the ' +
      'day you actually paid the vendor. The field is at the top of the ' +
      'receipt, next to Vendor invoice amount.';
  },
  approvalCrashed: function(errMessage) {
    return ' \u2014 something unexpected happened while approving this ' +
      'receipt. The receipt didn\'t go through. Send Cimon a screenshot ' +
      'of the error below and try again after she\'s checked in.\n\n' +
      'Technical detail: ' + (errMessage || 'unknown');
  },
  vendorMismatch: function(vendorName) {
    return ' \u2014 the vendor on this receipt ("' + (vendorName || '?') + '") ' +
      'doesn\u2019t match any vendor in our directory. You can fix this ' +
      'in one of two ways:\n\n' +
      '\u2022 If "' + (vendorName || '?') + '" is just a different spelling ' +
      'of an existing vendor, click the VENDOR field on this receipt and ' +
      'pick the right one from the list. We\u2019ll remember the alias so ' +
      'next time it matches automatically.\n\n' +
      '\u2022 If this is a brand new vendor, click the VENDOR field and ' +
      'choose "+ Add new vendor" to add them to the directory.\n\n' +
      'Once linked, re-approve the receipt.';
  },
  ccFeeInsertFailed: function(reason) {
    return ' \u2014 the main payment for this receipt was recorded, but ' +
      'the credit card fee couldn\u2019t be saved. The vendor payment is ' +
      'in the system; only the CC fee piece is missing.\n\n' +
      'Click Approve again to retry \u2014 it won\u2019t duplicate the ' +
      'main payment.\n\n' +
      'Technical detail: ' + (reason || 'unknown');
  },
  genericDbError: function(reason) {
    return ' \u2014 something\u2019s not right on our end that Cimon needs ' +
      'to look at. Re-approving won\u2019t fix it. Send Cimon a screenshot ' +
      'of this receipt and the error below.\n\n' +
      'Technical detail: ' + (reason || 'unknown');
  },
  itemUpdatePartialFailure: function(failedCount, itemList) {
    return ' \u2014 the receipt is approved and the payment is recorded, ' +
      'but we couldn\u2019t update the order-placed date on ' + failedCount +
      ' item(s): ' + itemList + '.\n\n' +
      'You\u2019ll need to set the order date manually on these items from ' +
      'the Tracker or item detail. Then advance their status if needed.\n\n' +
      'This is rare — usually means an item was deleted from the catalog ' +
      'or moved to a status that locks editing. This won\u2019t fix ' +
      'itself \u2014 handle the items individually.';
  },
  approvedSummary: function(summaryLines) {
    return ' \u2014 receipt approved.\n\n' + (summaryLines || []).join('\n');
  },
  // The per-item fee record didn't get written. Surfaced because it used
  // to be silent: applyFeeChoicesToVendorPaymentItems returned
  // `skipped: '<reason>'` and NOTHING printed it — not an alert, not a
  // console line — so the write failed on every approve from 2026-06-24
  // to 2026-09-09 with no signal at all. Success stays silent (nobody
  // wants a row count); a skip never does again.
  feeShareSkipped: function(reason) {
    return 'The per-item fee record wasn’t written for this receipt. ' +
      'The receipt, the payment and the client’s bill are all correct ' +
      '— this is the internal per-item breakdown only. Send Cimon a ' +
      'screenshot.\n   Technical detail: ' + (reason || 'unknown');
  },
  billAsFeeNoOp: function() {
    return ' \u2014 the receipt was approved, but you picked \'Bill as fee\' ' +
      'on a line where the actual amount matches the estimate \u2014 so ' +
      'nothing was added to the client\u2019s bill (there was no delta to ' +
      'bill). If the actual amount really differs, double-check what you ' +
      'typed.';
  },
  refreshDatePaidMissing: function() {
    return ' \u2014 fill in the Date paid before refreshing. The vendor ' +
      'payment needs the actual day you paid. Field is at the top of the ' +
      'receipt detail.';
  },
  refreshCrashed: function(errMessage) {
    return ' \u2014 refresh failed unexpectedly. Send Cimon the error so ' +
      'she can sort it out.\n\nTechnical detail: ' + (errMessage || 'unknown');
  },
  refreshIncomplete: function(reason, failureAt) {
    return ' \u2014 refresh didn\u2019t complete. The receipt itself is ' +
      'fine, but the linked vendor payment record might be in a half-written ' +
      'state. Send Cimon a screenshot \u2014 she\u2019ll check and fix.\n\n' +
      'Technical detail: ' + (reason || 'unknown') +
      (failureAt ? ' (' + failureAt + ')' : '');
  },
  refreshComplete: function(summary) {
    return ' \u2014 refresh complete. ' + (summary || '') + '.';
  },
  // Fee-confirmation backstop (2026-07-02) -- fires if approve() is
  // reached while estimated fees are still unconfirmed (the toolbar
  // button is already disabled in this state; this is the safety net).
  feesUnconfirmed: function(labels) {
    var list = (labels || []).join(', ');
    return ' \u2014 one more step before approving: confirm the fees. ' +
      'Go to the Fee details step \u2014 the estimated fees for this ' +
      'order are already filled in' +
      (list ? ' (' + list + ')' : '') +
      '. If they match the vendor\u2019s bill, click Confirm fees. ' +
      'If not, correct the amounts first.';
  },
};

// ── Confirmation prompts (window.confirm text) ───────────────────────────
export var confirm = {
  deleteReceipt:
    'Delete this receipt permanently? (The file itself stays in storage — ' +
    'only the database record is removed.)',
  // One-click reject (replaces the prior reason-input flow per Cimon's
  // 2026-06-27 direction — row #13 of the v2 copy audit).
  rejectReceipt: 'Reject this receipt?',
  // Absorb (2026-08-23 billing simplification): the firm eats the cost.
  absorbReceipt:
    "Don't bill this to the client? It stays on the books as a firm " +
    'cost. You can restore it later.',
};

// ── PrimerCell content (onboarding cards at top of Receipts tab) ────────
export var primer = {
  vendorInvoices: {
    title: 'Vendor invoices',
    body:
      'Deposit payment / Final payment / Full 100% payment / Shipping fee — ' +
      'auto-linked to the matching PO. Picking the right type matters: it ' +
      'drives the Ready to invoice signal and the reconciliation view.',
  },
  reimbursables: {
    title: 'Client charge',
    body:
      'Storage, travel, site visits — billed to client at cost + 15%. ' +
      'Private to submitter, owner, and bookkeeper.',
  },
  whatsNext: {
    title: "What's next",
    // QB push reference REMOVED per Rule 5/6 — Gilda doesn't push receipts
    // to QB; Dan enters vendor activity into QB through his own bookkeeping.
    body: 'Gilda reads it, you approve, the payment is recorded.',
  },
};

// ── Empty states ────────────────────────────────────────────────────────
export var emptyState = {
  inboxNoReceiptsSubmitterOnly:
    'You haven\u2019t submitted any receipts yet \u2014 drop one on the left.',
  inboxNoReceiptsAll:
    'No receipts yet \u2014 drop one on the left to get started.',
  filterNoMatch: 'Nothing matches this filter.',
};

// ── Drop zone ───────────────────────────────────────────────────────────
export var dropzone = {
  dragHeading: 'Drop to read receipt',
  dragSubhead: 'JPG, PNG, or PDF \u00b7 multiple files supported',
  cardHeading: 'Drop a receipt or click to choose',
};

// ── File preview ────────────────────────────────────────────────────────
export var preview = {
  // Eyebrow above the file preview pane in the receipt detail modal —
  // makes it explicit that the left pane is the actual receipt PDF.
  eyebrow: 'RECEIPT PDF',
  // Manual (no-document) receipts — cash tips etc. recorded without a scan.
  eyebrowNoFile: 'NO DOCUMENT',
};

// ── Kind cards (Step 1 of the wizard) ───────────────────────────────────
// Phase 2 of the receipt UI overhaul (2026-06-27) replaced the small
// dropdown with these large clickable cards. Each card carries:
//   - title:     short identifier shown at the top
//   - body:      plain-language description of what this kind means
//   - example:   a "e.g." scenario the bookkeeper will recognize
//   - isProject: project-related cards wear the fuchsia accent on select;
//                non-project (Office) wears the plain INK_1 ink accent.
//
// Strings here so future copy edits (the mockup v9 still has open
// drift on whether titles should drop the "payment" suffix; surfaced
// 2026-06-28 fidelity sweep) land in one place.
// Plain-language pass 2026-08-23 (Cimon: "be really really clear about
// which receipt types they are"). Every card answers three questions in
// order: WHO got paid, WHAT this payment was, and WHAT HAPPENS NEXT in
// the system. The DB enum values never change — only the words.
export var kindCards = [
  { value: 'vendor_deposit',       title: 'Paid a vendor — deposit on an order',
    body:    'Part of a vendor’s invoice, paid upfront (usually 50%) to start an order on a PO.',
    example: 'e.g. Wired Visual Comfort 50% on the sconce order; balance due before shipping.',
    isProject: true },
  { value: 'vendor_final',         title: 'Paid a vendor — final balance',
    body:    'The rest of a vendor’s invoice — goods ready to ship. A PO can have several if items ship in batches.',
    example: 'e.g. Visual Comfort invoices the remaining balance when C1 + C4 are ready to ship.',
    isProject: true },
  { value: 'vendor_full_payment',  title: 'Paid a vendor — in full, one payment',
    body:    'The full invoice paid in one go — usually in-stock goods.',
    example: 'e.g. Holland & Sherry fabric in stock; pay the full amount on order.',
    isProject: true },
  { value: 'vendor_fee',           title: 'Paid a shipping / freight / customs bill',
    body:    'Moving or importing the goods — not the goods themselves.',
    example: 'e.g. Yellow Freight crating + delivery; FedEx customs broker invoice.',
    isProject: true },
  { value: 'project_reimbursable', title: 'Project expense — bill it back to the client',
    body:    'A project cost the client reimburses at cost + 15% — travel, storage, site visits, hotels.',
    bodyStaff: 'A project cost the client reimburses — travel, storage, site visits, hotels.',
    example: 'e.g. Site visit to Hoback — airline + hotel + meals + ground transport.',
    isProject: true },
  { value: 'merchandise_direct',   title: 'Bought merchandise directly — no PO',
    body:    'An item bought for the client outside the PO process. The client is billed on the item’s final invoice — never from this receipt.',
    example: 'e.g. UL listing service + shipping for a custom fixture, paid before any client invoice.',
    isProject: true },
  { value: 'personal',             title: 'Office expense — the firm’s own cost',
    body:    'Firm overhead — never billed to any client. Supplies, software, subscriptions.',
    example: 'e.g. Adobe Creative Cloud monthly subscription.',
    isProject: false },
];

// ── Per-step guidance (2026-08-23 — Cimon: "walk the user through each
// step with simple but very clear language"). Keyed by kind, then by
// wizard step NUMBER. Rendered as a quiet instruction line at the top
// of the step pane. Defined for merchandise_direct now; other kinds can
// join in the broader wizard copy audit.
export var stepGuidance = {
  merchandise_direct: {
    1: 'Merchandise bought directly — no PO. The client pays via the item’s final invoice.',
    2: 'The date printed on the receipt — not today.',
    3: 'Enter exactly what the paper says, then link the item. The link never changes any numbers.',
    4: 'Approving files the firm’s record — nothing is billed from here.',
  },
};

// ── Running tab on the receipt detail ───────────────────────────────────
export var runningTab = {
  poTotal: function(poRef) { return 'PO total (' + (poRef || '') + ')'; },
  paidBefore: 'Paid before this receipt',
  thisReceipt: 'This receipt (paying the vendor)',
  paidAfter: 'Total paid after this receipt',
  // Status line — Cimon's direction (row #46): label as "Paid more than
  // expected" instead of "Over PO estimate" to keep it informational, not
  // error-flavored. The PO is still fully paid; this is a non-alarming note.
  status: {
    closes: 'PO fully paid \u2713',
    over: 'Paid more than expected',
    remaining: 'Still owed',
  },
  // Note: the math handles overpayment correctly — isPoFullyPaid uses
  // paidTotal >= expectedTotal − tolerance, so overpaying still flips the
  // Ready-to-invoice signal. This label is purely cosmetic.
};
